import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { ResultOfDebtorIndex } from '../../../api/client';
import DebtorIcon from '../../debtorOverview/presentation/DebtorIcon';
import { formatNationalIdentityNumber, formatPhoneNumber } from '../../../components/Utils';
import { history } from '../../../AppHistory';
import { actionCreators as ConversationSupervisingStoreActionCreators } from '../../../store/ConversationSupervisorStore';
import { actionCreators as DebtorClaimsStoreActionCreators } from '../../../store/DebtorClaimsStore';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import DebtorSearchResultReason from './DebtorSearchResultReason';
import PowerOfAttorney from '../../../components/powerOfAttorney/PowerOfAttorney';
import { actionCreators as DebtorSearchActionCreators } from '../../../store/DebtorSearchStore';
import DebtorClaimSystemBadge from '../../debtorOverview/presentation/DebtorClaimSystemBadge';

interface Props {
  item: ResultOfDebtorIndex;
  resetConversationSupervisor: typeof ConversationSupervisingStoreActionCreators.resetConversationSupervisor;
  resetDebtorClaims: typeof DebtorClaimsStoreActionCreators.resetDebtorClaims;
  selectDebtor: typeof DebtorSearchActionCreators.selectDebtor;
  selectCaseNumber: typeof DebtorSearchActionCreators.selectCaseNumber;
}

export default function DebtorSearchResultItem(props: Props) {
  if (!props.item.document) return null;

  const handleRedirectToDebtor = () => {
    props.resetConversationSupervisor();
    props.resetDebtorClaims();
    props.selectDebtor(props.item);
    history.push(`/debtorlookup`);
  };

  const handleRedirectToClaim = (caseNumber: string) => {
    props.resetConversationSupervisor();
    props.resetDebtorClaims();
    props.selectDebtor(props.item);
    props.selectCaseNumber(caseNumber);
    history.push(`/claimlookup`);
  }

  
  const caseNumber = props.item.highlights?.caseNumbers?.[0]?.replace(/<[^>]+>/g, "");
  const showSingleClaimViewButtons = props.item.document.isOrganization && caseNumber !== undefined;
  const numberOfClaims = props.item.document?.invoiceNumbers?.length ?? 0;

  return (
    <Card raised={true} sx={{ width: '780px', margin: '10px auto' }}>
      <CardContent sx={{ minHeight: '100px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', mb: '14px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%'}} >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}><DebtorIcon isOrganization={props.item.document!.isOrganization} />{props.item.document!.name}</Typography>
            <Typography align='right' sx={{ mt: '8px', mr: '12px', minWidth: '64px', color: numberOfClaims === 0 ? 'red' : 'black' }}>{numberOfClaims > 0 ? '~' + numberOfClaims : 'Ingen'} krav</Typography>
          </Box>
          <Typography sx={{ mt: '-6px', fontSize: '1.25rem' }}>{props.item.document!.isOrganization ? props.item.document!.organizationNumber : formatNationalIdentityNumber(props.item.document!.nationalIdentityNumber) || props.item.document!.dateOfBirth}</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ mb: '10px' }}>
            <Box sx={{ mb: '10px' }}>
              {props.item.document!.extendedAddressLines?.map((item, i) => 
                <Typography key={i}>{item}</Typography>
              )}
              <Typography>{props.item.document!.street}</Typography>
              <Typography>{props.item.document!.postCode} {props.item.document!.postArea}</Typography>
              <Typography>{props.item.document!.country}</Typography>
            </Box>
            <Box>
              {props.item.document!.phoneNumbers && props.item.document!.phoneNumbers!.map((item, i) => <Typography key={i}>{formatPhoneNumber(item)}</Typography>)}
              {props.item.document!.email && <Typography>{props.item.document!.email}</Typography>}
            </Box>
          </Box>

          <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'end',
            mb: '4px'
          }}>
            {
              showSingleClaimViewButtons ? (
                <>
                  <Button variant="contained" onClick={handleRedirectToDebtor}>
                    Vis alle krav
                  </Button>
                  <Button variant="contained" onClick={() => handleRedirectToClaim(caseNumber)}>
                    Vis {caseNumber}
                  </Button>
                </>
              ) : (
                <>
                  <PowerOfAttorney debtor={props.item} />
                  <Button variant="contained" onClick={handleRedirectToDebtor}>
                    Annen ringer
                  </Button>
                  <Button variant="contained" onClick={handleRedirectToDebtor}>
                    Ringer selv
                  </Button>
                </>
              )}
          </Box>
        </Box>
      

        <Divider />
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <DebtorSearchResultReason highlights={props.item.highlights} />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ padding: '8px 6px 4px 0px' }}>
              {props.item.document!.relatedDebtorIds && props.item.document!.relatedDebtorIds!.filter(x => x.startsWith('leap')).length > 0 && <DebtorClaimSystemBadge system={'leap'} />}
              {props.item.document!.relatedDebtorIds && props.item.document!.relatedDebtorIds!.filter(x => x.startsWith('mmsys')).length > 0 && <DebtorClaimSystemBadge system={'mmsys'} />}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
